import React, { type ReactNode } from 'react';

import type { ResultEventAttributes } from '../../common/utils/events/advanced-search-result-interacted';
import { useFireContextMenuButtonClickedWithAttributes } from '../events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClickedWithAttributes } from '../events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClickedWithAttributes } from '../events/use-fire-copy-link-button-clicked';
import { useFireResultClickedWithAttributes } from '../events/use-fire-result-clicked';
import { useFireResultCtxMenuClickedWithAttributes } from '../events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClickedWithAttributes } from '../events/use-fire-summarize-in-chat-button-clicked';
import { useAnalyticsDispatcher } from './analytics-dispatcher';
import {
	getResultAttributesAtRender,
	type PreparedResultAttributes,
} from './prepared-result-attributes';
import type { ResultAnalyticsCallbacks } from './result-analytics-callbacks';

type Props = ResultEventAttributes & {
	preparedResultAttributes: PreparedResultAttributes;
	children: (callbacks: ResultAnalyticsCallbacks) => ReactNode;
};

/**
 * Prop-driven treatment path. The dispatcher is the only context-bound dependency: it must bind to
 * the nearest result-row AnalyticsContext. All search state is prepared above the result list.
 */
export const SharedResultAnalytics = ({
	children,
	preparedResultAttributes,
	...resultEventAttributes
}: Props): React.JSX.Element => {
	const sharedEventAttributes = getResultAttributesAtRender(preparedResultAttributes);
	const { fireAnalyticsEvent } = useAnalyticsDispatcher();
	const eventCallbacks = { fireAnalyticsEvent };
	const onResultClicked = useFireResultClickedWithAttributes(
		sharedEventAttributes,
		eventCallbacks,
		{
			metadata: resultEventAttributes.metadata,
			position: resultEventAttributes.position,
			socialSignal: resultEventAttributes.socialSignal,
			isImpersonated: resultEventAttributes.isImpersonated,
			is3PClickIngestionEnabled: resultEventAttributes.is3PClickIngestionEnabled,
			intentDetectionTriggered: resultEventAttributes.intentDetectionTriggered,
		},
	);
	const onResultContextMenu = useFireResultCtxMenuClickedWithAttributes(
		sharedEventAttributes,
		eventCallbacks,
		{
			metadata: resultEventAttributes.metadata,
			position: resultEventAttributes.position,
			socialSignal: resultEventAttributes.socialSignal,
			isImpersonated: resultEventAttributes.isImpersonated,
			is3PClickIngestionEnabled: resultEventAttributes.is3PClickIngestionEnabled,
			intentDetectionTriggered: resultEventAttributes.intentDetectionTriggered,
		},
	);
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClickedWithAttributes(
		sharedEventAttributes,
		eventCallbacks,
		{ location: 'searchResultTitle' },
	);
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClickedWithAttributes(
		sharedEventAttributes,
		eventCallbacks,
		{
			location: 'searchResult',
			metadata: resultEventAttributes.metadata,
			position: resultEventAttributes.position,
		},
	);
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClickedWithAttributes(
		sharedEventAttributes,
		eventCallbacks,
	);
	const onContextMenuButtonClicked = useFireContextMenuButtonClickedWithAttributes(
		sharedEventAttributes,
		eventCallbacks,
	);

	return (
		<>
			{children({
				onResultClicked,
				onResultContextMenu,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
				onContextMenuCopyButtonClicked,
				onContextMenuButtonClicked,
			})}
		</>
	);
};
